import { memo, useState, useRef, useEffect, useCallback } from 'react';
import {
  UnderlinedButton,
  TertiaryButton,
} from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import styles from './HorizontalFilterNavDropdown.module.scss';
import { useHorizontalFilterNavDropdownData } from './data/useHorizontalFilterNavDropdownData';
import { Facet, FacetValue } from '../../utils/types/types';
import { ChevronIcon } from './ChevronIcon';
import FacetItem from '../facets/facet-item/FacetItem';
import FacetSearch from '../facets/facet-search/FacetSearch';

export type HorizontalFilterNavDropdownProps = {
  topFilters?: Facet[];
};

const SEARCH_LENGTH = 21;
const INITIAL_NUMBER_TO_SHOW = 8;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 10;

const toKebabCase = (str: string): string =>
  str.toLowerCase().replace(/\s+/g, '-');

const getFilteredValues = (
  values: FacetValue[],
  searchTerm: string
): FacetValue[] => {
  if (!searchTerm) return values;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return values.filter((value) =>
    value.label?.toLowerCase().includes(lowerSearchTerm)
  );
};

export const HorizontalFilterNavDropdown =
  memo<HorizontalFilterNavDropdownProps>(({ topFilters = [] }) => {
    const { router, isHorizontalFilterNavDropdownFlagOn } =
      useHorizontalFilterNavDropdownData();

    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
    const [expandedFacets, setExpandedFacets] = useState<
      Record<string, boolean>
    >({});

    const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const { href } = router;

    const handleDropdownToggle = useCallback((facetName: string) => {
      setOpenDropdown((prev) => (prev === facetName ? null : facetName));
    }, []);

    const handleSearch = useCallback((facetName: string, value: string) => {
      setSearchTerms((prev) => ({ ...prev, [facetName]: value }));
    }, []);

    const handleClearSearch = useCallback((facetName: string) => {
      setSearchTerms((prev) => ({ ...prev, [facetName]: '' }));
    }, []);

    const toggleShowMore = useCallback((facetName: string) => {
      setExpandedFacets((prev) => ({
        ...prev,
        [facetName]: !prev[facetName],
      }));
    }, []);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const clickedOutside = Object.values(dropdownRefs.current)
          .filter(Boolean)
          .every((ref) => ref && !ref.contains(event.target as Node));

        if (clickedOutside) {
          setOpenDropdown(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    if (!isHorizontalFilterNavDropdownFlagOn || topFilters.length === 0) {
      return null;
    }

    return (
      <div className={styles.filterButtons}>
        {topFilters.map((facet: Facet & { hasAppliedValues: boolean }) => {
          const facetName = facet.name;
          const facetKebab = toKebabCase(facetName);
          const isOpen = openDropdown === facetName;
          const dropdownId = `dropdown-${facetName}`;
          const buttonId = `filter-button-${facetKebab}`;

          return (
            <div
              key={facetName}
              className={styles.filterDropdownContainer}
              ref={(el) => {
                dropdownRefs.current[facetName] = el;
              }}
            >
              <TertiaryButton
                small
                onClick={() => handleDropdownToggle(facetName)}
                data-testid={buttonId}
                className={styles.filterButton}
                aria-expanded={isOpen}
                aria-controls={dropdownId}
                aria-label={`Filter by ${facet.name}${facet.hasAppliedValues ? ' (has selections)' : ''}`}
                iconPositionRight
                IconComponent={
                  <ChevronIcon
                    isOpen={isOpen}
                    testId={`chevron-${facetKebab}`}
                  />
                }
              >
                <span>{facet.label || facet.name}</span>
              </TertiaryButton>
              {isOpen && (
                <div
                  id={dropdownId}
                  className={styles.dropdownContent}
                  data-testid={`filter-dropdown-content-${facetKebab}`}
                >
                  {(() => {
                    const searchTerm = searchTerms[facetName] || '';
                    const isExpanded = expandedFacets[facetName] || false;
                    const facetValues = facet.values || [];

                    const isFacetListSearchable =
                      facetValues.length >= SEARCH_LENGTH;
                    const itemsToDisplay = getFilteredValues(
                      facetValues,
                      searchTerm
                    );

                    const numberOfValuesToShow =
                      itemsToDisplay.length >
                        MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING && !isExpanded
                        ? INITIAL_NUMBER_TO_SHOW
                        : itemsToDisplay.length;

                    const numberOfHiddenValues =
                      itemsToDisplay.length >
                      MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING
                        ? itemsToDisplay.length - INITIAL_NUMBER_TO_SHOW
                        : 0;

                    const isShowMoreOrFewerVisible =
                      itemsToDisplay.length >
                      MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

                    return (
                      <div className={styles.filterDropdownContent}>
                        {isFacetListSearchable && (
                          <div className={styles.searchSection}>
                            <FacetSearch
                              onSearch={
                                ((value: string) =>
                                  handleSearch(facetName, value)) as () => void
                              }
                              onClear={() => handleClearSearch(facetName)}
                              search={searchTerm}
                              label={facet.label || facet.name}
                              isFilterDrawer
                            />
                          </div>
                        )}

                        <div className={styles.facetItems}>
                          {itemsToDisplay
                            .slice(0, numberOfValuesToShow)
                            .map((value, index) => (
                              <FacetItem
                                key={value.facetId || index}
                                value={value}
                                facet={facet}
                                href={href}
                                isCheckbox
                                isFilterDrawer
                                listItem={false}
                              />
                            ))}
                        </div>

                        {isShowMoreOrFewerVisible && (
                          <div className={styles.showMoreSection}>
                            <UnderlinedButton
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleShowMore(facetName);
                              }}
                              data-testid={`show-${isExpanded ? 'fewer' : 'more'}-button`}
                            >
                              {isExpanded
                                ? 'Show fewer'
                                : `Show ${numberOfHiddenValues} more`}
                            </UnderlinedButton>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  });

HorizontalFilterNavDropdown.displayName = 'HorizontalFilterNavDropdown';
